<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { nextTick, ref } from "vue";

import { TuiInputSwitch } from "@vm/tui";

import AddObjectSelect from "./add-object-select.vue";

import { useComparisonConfigStore } from "@/config/config-store";

import type { StoredObject } from "../comparison-store";

const props = defineProps<{
  objects: StoredObject[];
  typeBoundaryIndex: number;
  columnLayout: {
    gridTemplateColumns: string;
    minWidth: string;
    stickyWidth: number;
  };
  objectColumnsStyle: {
    gridTemplateColumns: string;
    minWidth: string;
  };
  hideIdentical: boolean;
}>();

const emit = defineEmits<{
  "update:hideIdentical": [value: boolean];
  move: [fromIndex: number, toIndex: number];
  remove: [name: string];
}>();

const { colors, fontSize } = storeToRefs(useComparisonConfigStore());

const swappingIndices = ref<Set<number>>(new Set());

function objectColumnStyle(index: number) {
  if (index !== 0) return undefined;
  return {
    left: `${props.columnLayout.stickyWidth}px`,
    backgroundColor: colors.value.headerBackground,
  };
}

function onMove(fromIndex: number, toIndex: number) {
  swappingIndices.value = new Set([fromIndex, toIndex]);
  emit("move", fromIndex, toIndex);
  nextTick(() => {
    setTimeout(() => {
      swappingIndices.value = new Set();
    }, 300);
  });
}
</script>

<template>
  <div
    class="sticky top-0 z-30 flex shadow"
    :style="{
      backgroundColor: colors.headerBackground,
      minWidth: columnLayout.minWidth,
    }"
  >
    <div
      class="sticky left-0 z-20 flex shrink-0 flex-col justify-center gap-2 px-4 py-3"
      :style="{
        width: `${columnLayout.stickyWidth}px`,
        backgroundColor: colors.headerBackground,
      }"
    >
      <label
        class="flex cursor-pointer select-none items-center gap-2 text-xs text-white/70"
      >
        <TuiInputSwitch
          :model-value="hideIdentical"
          @update:model-value="emit('update:hideIdentical', $event)"
        />
        <span>Alleen verschillen</span>
      </label>
      <AddObjectSelect dark size="small" />
    </div>
    <div
      class="grid flex-1"
      :style="{ ...objectColumnsStyle, fontSize: `${fontSize}px` }"
    >
      <div
        v-for="(obj, index) in objects"
        :key="obj.name.value"
        class="relative px-4 py-3 text-center transition-all duration-300"
        :class="[
          { 'header-swap': swappingIndices.has(index) },
          index === 0 ? 'sticky z-20' : '',
          index === typeBoundaryIndex ? 'border-l-2 border-white/30' : '',
        ]"
        :style="objectColumnStyle(index)"
      >
        <div class="flex flex-col items-center gap-0.5">
          <span class="truncate font-semibold text-white">
            {{ obj.name.value }}
          </span>
          <span
            v-if="obj.type?.value"
            class="rounded-full px-2 py-0.5 text-xs font-medium text-white"
            :style="{
              backgroundColor:
                colors.typeColors[obj.type.value] ??
                colors.accordionHeaderBackground,
            }"
          >
            {{ obj.type.value }}
          </span>
          <div
            v-if="obj.value?.value"
            class="flex flex-col items-center gap-0.5"
          >
            <span
              v-for="part in obj.value.value.split(' - ')"
              :key="part"
              class="rounded-full px-2 py-0.5 font-medium text-white"
            >
              {{ part }}
            </span>
          </div>
          <div class="mt-1 flex gap-1">
            <button
              :disabled="
                index === 0 ||
                objects[index - 1]?.type?.value !== obj.type?.value
              "
              class="flex h-6 w-6 items-center justify-center rounded-full text-gray-300 transition-colors hover:bg-white/15 hover:text-white disabled:opacity-30"
              title="Naar links"
              @click="onMove(index, index - 1)"
            >
              <i class="pi pi-chevron-left text-xs" />
            </button>
            <button
              :disabled="
                index === objects.length - 1 ||
                objects[index + 1]?.type?.value !== obj.type?.value
              "
              class="flex h-6 w-6 items-center justify-center rounded-full text-gray-300 transition-colors hover:bg-white/15 hover:text-white disabled:opacity-30"
              title="Naar rechts"
              @click="onMove(index, index + 1)"
            >
              <i class="pi pi-chevron-right text-xs" />
            </button>
            <button
              class="flex h-6 w-6 items-center justify-center rounded-full text-red-400 transition-colors hover:bg-red-500/20 hover:text-red-300"
              title="Verwijder uit vergelijking"
              @click="emit('remove', obj.key)"
            >
              <i class="pi pi-times text-xs" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.header-swap {
  animation: swap-highlight 0.3s ease;
}

@keyframes swap-highlight {
  0% {
    opacity: 0.4;
    transform: scale(0.95);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.02);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
